import React, { useState } from 'react';

interface FollowupInputProps {
  readingId: string | null;
  onResponse: (response: string) => void;
}

const MAX_FOLLOWUPS = 3;

const FollowupInput: React.FC<FollowupInputProps> = ({ readingId, onResponse }) => {
  const [question, setQuestion] = useState('');
  const [followupsUsed, setFollowupsUsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!readingId) return null;

  const remaining = MAX_FOLLOWUPS - followupsUsed;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || remaining <= 0 || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/followup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ readingId, question: question.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '追問失敗');
      }

      // Read followups-used from response headers
      const usedHeader = response.headers.get('X-Followups-Used');
      if (usedHeader) setFollowupsUsed(parseInt(usedHeader, 10));

      // Stream the response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ') && line !== 'data: [DONE]') {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.text) {
                  fullText += data.text;
                  onResponse(fullText);
                }
              } catch {
                // Skip malformed SSE lines
              }
            }
          }
        }
      }

      setQuestion('');
      setFollowupsUsed(prev => prev + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : '追問失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 border-t border-slate-700 pt-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-amber-400 font-medium">追問大師</h3>
        <span className={`text-sm ${remaining > 0 ? 'text-slate-400' : 'text-red-400'}`}>
          剩餘追問次數: {remaining}/{MAX_FOLLOWUPS}
        </span>
      </div>

      {remaining > 0 ? (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="請輸入您的追問..."
            disabled={loading}
            className="flex-1 bg-slate-700 text-white rounded px-3 py-2 placeholder-slate-400 border border-slate-600 focus:border-amber-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            className="px-4 py-2 bg-amber-500 text-black font-medium rounded hover:bg-amber-400 disabled:bg-slate-600 disabled:text-slate-400 transition"
          >
            {loading ? '問卦中...' : '追問'}
          </button>
        </form>
      ) : (
        <p className="text-slate-500 text-sm">已達追問上限</p>
      )}

      {error && <p className="mt-2 text-red-400 text-sm">{error}</p>}
    </div>
  );
};

export default FollowupInput;
